import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../api';
import logger from '../utils/logger';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  MapPin, 
  FileText,
  Users,
  CheckCircle2,
  Download,
  Building2,
  X
} from 'lucide-react';
import OrganisationsPanel from '../components/OrganisationsPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const Admin = () => {
  const [arrondissements, setArrondissements] = useState([]);
  const [zoneTemplates, setZoneTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [secteurExports, setSecteurExports] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showArrModal, setShowArrModal] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [editingArr, setEditingArr] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  
  // Form states
  const [arrForm, setArrForm] = useState({ name: '', code: '', description: '', is_active: true });
  const [zoneForm, setZoneForm] = useState({
    zone_code: '',
    zone_name: '',
    arrondissement_id: '',
    permitted_uses: [],
    max_height_meters: '',
    max_height_storeys: '',
    front_setback: '',
    side_setback_left: '',
    side_setback_right: '',
    rear_setback: '',
    lot_coverage_percent: '',
    floor_area_ratio: '',
    parking_ratio: '',
    density_units_per_hectare: '',
    special_constraints: '',
    notes: '',
    is_validated: false
  });

  const loadData = useCallback(async () => {
    try {
      const [arrRes, zonesRes, usersRes, exportsRes] = await Promise.all([
        adminAPI.listArrondissements(),
        adminAPI.listZoneTemplates(),
        adminAPI.listUsers().catch(() => ({ data: [] })),
        adminAPI.listSecteurExports().catch(() => ({ data: [] }))
      ]);
      setArrondissements(arrRes.data);
      setZoneTemplates(zonesRes.data);
      setUsers(usersRes.data);
      setSecteurExports(exportsRes.data);
    } catch (error) {
      logger.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Arrondissement handlers
  const openArrModal = (arr = null) => {
    if (arr) {
      setEditingArr(arr);
      setArrForm({
        name: arr.name,
        code: arr.code,
        description: arr.description || '',
        is_active: arr.is_active
      });
    } else {
      setEditingArr(null);
      setArrForm({ name: '', code: '', description: '', is_active: true });
    }
    setShowArrModal(true);
  };

  const saveArrondissement = async () => {
    try {
      if (editingArr) {
        await adminAPI.updateArrondissement(editingArr.id, arrForm);
        toast.success('Arrondissement mis à jour');
      } else {
        await adminAPI.createArrondissement(arrForm);
        toast.success('Arrondissement créé');
      }
      setShowArrModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const deleteArrondissement = async (id) => {
    if (!window.confirm('Supprimer cet arrondissement?')) return;
    try {
      await adminAPI.deleteArrondissement(id);
      toast.success('Arrondissement supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Zone template handlers
  const openZoneModal = (zone = null) => {
    if (zone) {
      setEditingZone(zone);
      setZoneForm({
        ...zone,
        permitted_uses: zone.permitted_uses || [],
        max_height_meters: zone.max_height_meters || '',
        max_height_storeys: zone.max_height_storeys || '',
        front_setback: zone.front_setback || '',
        side_setback_left: zone.side_setback_left || '',
        side_setback_right: zone.side_setback_right || '',
        rear_setback: zone.rear_setback || '',
        lot_coverage_percent: zone.lot_coverage_percent || '',
        floor_area_ratio: zone.floor_area_ratio || '',
        parking_ratio: zone.parking_ratio || '',
        density_units_per_hectare: zone.density_units_per_hectare || '',
        special_constraints: zone.special_constraints || '',
        notes: zone.notes || ''
      });
    } else {
      setEditingZone(null);
      setZoneForm({
        zone_code: '',
        zone_name: '',
        arrondissement_id: arrondissements[0]?.id || '',
        permitted_uses: [],
        max_height_meters: '',
        max_height_storeys: '',
        front_setback: '',
        side_setback_left: '',
        side_setback_right: '',
        rear_setback: '',
        lot_coverage_percent: '',
        floor_area_ratio: '',
        parking_ratio: '',
        density_units_per_hectare: '',
        special_constraints: '',
        notes: '',
        is_validated: false
      });
    }
    setShowZoneModal(true);
  };

  const saveZoneTemplate = async () => {
    try {
      const data = {
        ...zoneForm,
        max_height_meters: zoneForm.max_height_meters ? parseFloat(zoneForm.max_height_meters) : null,
        max_height_storeys: zoneForm.max_height_storeys ? parseInt(zoneForm.max_height_storeys) : null,
        front_setback: zoneForm.front_setback ? parseFloat(zoneForm.front_setback) : null,
        side_setback_left: zoneForm.side_setback_left ? parseFloat(zoneForm.side_setback_left) : null,
        side_setback_right: zoneForm.side_setback_right ? parseFloat(zoneForm.side_setback_right) : null,
        rear_setback: zoneForm.rear_setback ? parseFloat(zoneForm.rear_setback) : null,
        lot_coverage_percent: zoneForm.lot_coverage_percent ? parseFloat(zoneForm.lot_coverage_percent) : null,
        floor_area_ratio: zoneForm.floor_area_ratio ? parseFloat(zoneForm.floor_area_ratio) : null,
        density_units_per_hectare: zoneForm.density_units_per_hectare ? parseFloat(zoneForm.density_units_per_hectare) : null
      };
      
      if (editingZone) {
        await adminAPI.updateZoneTemplate(editingZone.id, data);
        toast.success('Modèle mis à jour');
      } else {
        await adminAPI.createZoneTemplate(data);
        toast.success('Modèle créé');
      }
      setShowZoneModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const deleteZoneTemplate = async (id) => {
    if (!window.confirm('Supprimer ce modèle de zone?')) return;
    try {
      await adminAPI.deleteZoneTemplate(id);
      toast.success('Modèle supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // User role handler
  const updateUserRole = async (userId, role) => {
    try {
      await adminAPI.updateUserRole(userId, role);
      toast.success('Rôle mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const downloadSecteursCsv = async (territoire) => {
    try {
      const response = await adminAPI.exportSecteursCsv(territoire);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `secteurs_${territoire}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="p-8" data-testid="admin-page">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl uppercase text-primary">
          Administration
        </h1>
        <p className="text-muted-foreground">
          Gérer les arrondissements, modèles de zone et utilisateurs
        </p>
        {secteurExports.map((territoire) => (
          <Button
            key={territoire}
            variant="outline"
            className="mt-4 mr-2"
            onClick={() => downloadSecteursCsv(territoire)}
            data-testid={`export-secteurs-${territoire}`}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV des secteurs ({territoire})
          </Button>
        ))}
      </div>

      <Tabs defaultValue="arrondissements">
        <TabsList className="mb-6">
          <TabsTrigger value="arrondissements" data-testid="tab-arr">
            <MapPin className="w-4 h-4 mr-2" />
            Arrondissements
          </TabsTrigger>
          <TabsTrigger value="zones" data-testid="tab-zones">
            <FileText className="w-4 h-4 mr-2" />
            Modèles de zone
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" />
            Utilisateurs
          </TabsTrigger>
          <TabsTrigger value="organisations" data-testid="tab-organisations">
            <Building2 className="w-4 h-4 mr-2" />
            Organisations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organisations">
          <OrganisationsPanel />
        </TabsContent>

        {/* Arrondissements Tab */}
        <TabsContent value="arrondissements">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading uppercase">Arrondissements</CardTitle>
              <Button onClick={() => openArrModal()} className="btn-primary" data-testid="add-arr-btn">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {arrondissements.map((arr) => (
                  <div key={arr.id} className="flex items-center justify-between py-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-accent">{arr.code}</span>
                        <span className="font-medium">{arr.name}</span>
                        {!arr.is_active && <Badge variant="secondary">Inactif</Badge>}
                      </div>
                      {arr.description && (
                        <p className="text-sm text-muted-foreground mt-1">{arr.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openArrModal(arr)}
                        data-testid={`edit-arr-${arr.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-destructive"
                        onClick={() => deleteArrondissement(arr.id)}
                        data-testid={`delete-arr-${arr.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {arrondissements.length === 0 && (
                  <p className="text-center py-8 text-muted-foreground">Aucun arrondissement</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Zone Templates Tab */}
        <TabsContent value="zones">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading uppercase">Modèles de zone</CardTitle>
              <Button onClick={() => openZoneModal()} className="btn-primary" data-testid="add-zone-btn">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {zoneTemplates.map((zone) => (
                  <div key={zone.id} className="flex items-center justify-between py-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold">{zone.zone_code}</span>
                        <span className="font-medium">{zone.zone_name}</span>
                        {zone.is_validated && (
                          <Badge variant="outline" className="text-success border-success">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Validé
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {zone.permitted_uses?.join(', ')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openZoneModal(zone)}
                        data-testid={`edit-zone-${zone.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-destructive"
                        onClick={() => deleteZoneTemplate(zone.id)}
                        data-testid={`delete-zone-${zone.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {zoneTemplates.length === 0 && (
                  <p className="text-center py-8 text-muted-foreground">Aucun modèle de zone</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading uppercase">Utilisateurs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between py-4">
                    <div>
                      <p className="font-medium">{user.full_name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Select
                        value={user.role}
                        onValueChange={(value) => updateUserRole(user.id, value)}
                      >
                        <SelectTrigger className="w-32" data-testid={`user-role-${user.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">Utilisateur</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {users.length === 0 && (
                  <p className="text-center py-8 text-muted-foreground">Aucun utilisateur</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Arrondissement Modal */}
      <Dialog open={showArrModal} onOpenChange={setShowArrModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {editingArr ? 'Modifier l\'arrondissement' : 'Nouvel arrondissement'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                value={arrForm.name}
                onChange={(e) => setArrForm({ ...arrForm, name: e.target.value })}
                placeholder="Ex: Rosemont–La Petite-Patrie"
                data-testid="arr-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Code *</Label>
              <Input
                value={arrForm.code}
                onChange={(e) => setArrForm({ ...arrForm, code: e.target.value })}
                placeholder="Ex: RPP"
                data-testid="arr-code"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={arrForm.description}
                onChange={(e) => setArrForm({ ...arrForm, description: e.target.value })}
                data-testid="arr-description"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={arrForm.is_active}
                onCheckedChange={(checked) => setArrForm({ ...arrForm, is_active: checked })}
                data-testid="arr-active"
              />
              <Label>Actif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArrModal(false)}>Annuler</Button>
            <Button onClick={saveArrondissement} className="btn-primary" data-testid="save-arr-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zone Template Modal */}
      <Dialog open={showZoneModal} onOpenChange={setShowZoneModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {editingZone ? 'Modifier le modèle' : 'Nouveau modèle de zone'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code de zone *</Label>
                <Input
                  value={zoneForm.zone_code}
                  onChange={(e) => setZoneForm({ ...zoneForm, zone_code: e.target.value })}
                  placeholder="Ex: H2-35-02"
                  data-testid="zone-code"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom de zone *</Label>
                <Input
                  value={zoneForm.zone_name}
                  onChange={(e) => setZoneForm({ ...zoneForm, zone_name: e.target.value })}
                  placeholder="Ex: Résidentiel moyenne densité"
                  data-testid="zone-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Arrondissement *</Label>
              <Select
                value={zoneForm.arrondissement_id}
                onValueChange={(value) => setZoneForm({ ...zoneForm, arrondissement_id: value })}
              >
                <SelectTrigger data-testid="zone-arr">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {arrondissements.map((arr) => (
                    <SelectItem key={arr.id} value={arr.id}>{arr.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Usages permis</Label>
              <Textarea
                value={zoneForm.permitted_uses.join(', ')}
                onChange={(e) => setZoneForm({ 
                  ...zoneForm, 
                  permitted_uses: e.target.value.split(',').map(u => u.trim()).filter(u => u)
                })}
                placeholder="Séparés par des virgules"
                data-testid="zone-uses"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hauteur max (m)</Label>
                <Input
                  type="number"
                  value={zoneForm.max_height_meters}
                  onChange={(e) => setZoneForm({ ...zoneForm, max_height_meters: e.target.value })}
                  data-testid="zone-height-m"
                />
              </div>
              <div className="space-y-2">
                <Label>Hauteur max (étages)</Label>
                <Input
                  type="number"
                  value={zoneForm.max_height_storeys}
                  onChange={(e) => setZoneForm({ ...zoneForm, max_height_storeys: e.target.value })}
                  data-testid="zone-height-storeys"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Marge avant (m)</Label>
                <Input
                  type="number"
                  value={zoneForm.front_setback}
                  onChange={(e) => setZoneForm({ ...zoneForm, front_setback: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Marge arrière (m)</Label>
                <Input
                  type="number"
                  value={zoneForm.rear_setback}
                  onChange={(e) => setZoneForm({ ...zoneForm, rear_setback: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Marge gauche (m)</Label>
                <Input
                  type="number"
                  value={zoneForm.side_setback_left}
                  onChange={(e) => setZoneForm({ ...zoneForm, side_setback_left: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Marge droite (m)</Label>
                <Input
                  type="number"
                  value={zoneForm.side_setback_right}
                  onChange={(e) => setZoneForm({ ...zoneForm, side_setback_right: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Taux implantation (%)</Label>
                <Input
                  type="number"
                  value={zoneForm.lot_coverage_percent}
                  onChange={(e) => setZoneForm({ ...zoneForm, lot_coverage_percent: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>COS</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={zoneForm.floor_area_ratio}
                  onChange={(e) => setZoneForm({ ...zoneForm, floor_area_ratio: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Stationnement</Label>
              <Input
                value={zoneForm.parking_ratio}
                onChange={(e) => setZoneForm({ ...zoneForm, parking_ratio: e.target.value })}
                placeholder="Ex: 1 case par logement"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={zoneForm.is_validated}
                onCheckedChange={(checked) => setZoneForm({ ...zoneForm, is_validated: checked })}
                data-testid="zone-validated"
              />
              <Label>Validé par un professionnel</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowZoneModal(false)}>Annuler</Button>
            <Button onClick={saveZoneTemplate} className="btn-primary" data-testid="save-zone-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
